import React from 'react';
import { Card, Button, Form as BtForm, InputGroup } from 'react-bootstrap';
import { LuBriefcase, LuMapPin, LuPhone, LuMail, LuArrowRight } from "react-icons/lu";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile, updateOnboardingStep } from '../../redux/reducers/authSlice';
import { createBusiness } from '../../redux/reducers/businessSlice';
import PhoneInput from '../../components/common/PhoneInput';

const SetupBusiness = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token, userDetail } = useSelector((state) => state.auth);
    const { loading: businessLoading } = useSelector((state) => state.business);

    React.useEffect(() => {
        if (!token) {
            navigate("/login");
            return;
        }
        if (userDetail && !userDetail.isVerified) {
            navigate("/onboarding/verify-notice");
            return;
        }
        if (userDetail?.hasCompletedOnboarding && userDetail?.onboardingStep >= 2) {
            navigate("/");
        }
    }, [token, userDetail, navigate]);

    const validationSchema = Yup.object({
        name: Yup.string().required("Business Name is required"),
        businessType: Yup.string().oneOf(
            ['retail', 'wholesale', 'services', 'manufacturing', 'ecommerce', 'other'],
            "Invalid Business Type"
        ).required("Required"),
        location: Yup.string().required("Address is required"),
        phone: Yup.object({
            countryCode: Yup.string().required("Required"),
            number: Yup.string().required("Phone number is required")
        }),
        businessEmail: Yup.string().email("Invalid email").required("Required"),
        countryOfOperation: Yup.string().oneOf(
            ['canada', 'usa', 'mexico', 'nepal'],
            "Invalid Country"
        ).required("Country is required"),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        const generateSlug = (text) => {
            return text.toLowerCase().trim()
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_-]+/g, '-')
                .replace(/^-+|-+$/g, '');
        };

        const payload = {
            name: values.name,
            slug: `${generateSlug(values.name)}-${Math.floor(1000 + Math.random() * 9000)}`,
            businessType: values.businessType,
            logo: "https://images.unsplash.com/photo-1554224154-26032fced8bd?w=200",
            location: values.location,
            contact: {
                email: values.businessEmail,
                phone: values.phone
            },
            countryOfOperation: values.countryOfOperation,
            masterCustomer: localStorage.getItem("masterCustomerId")
        };

        try {
            const resultAction = await dispatch(createBusiness(payload));

            if (createBusiness.fulfilled.match(resultAction)) {
                const newBusiness = resultAction.payload;
                const businessId = newBusiness?._id || newBusiness?.id;

                if (businessId) {
                    localStorage.setItem("activeBusinessId", businessId);

                    // Update onboarding to Step 4 (hasCompletedOnboarding: true)
                    await dispatch(updateOnboardingStep(4));

                    // Refresh the profile
                    if (typeof getProfile === 'function') {
                        await dispatch(getProfile());
                    }

                    // Navigate to dashboard
                    navigate(`/`, { replace: true });
                }
            }
        } catch (error) {
            console.error("Onboarding Navigation Error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="auth-wrapper d-flex align-items-center justify-content-center bg-light px-3" style={{ minHeight: '100vh' }}>
            <Card className="auth-card border-0 shadow-lg rounded-4 p-4" style={{ maxWidth: '500px', width: '100%' }}>
                <div className="text-center mb-4">
                    <h3 className="fw-bold text-dark">Setup Your Business</h3>
                    <p className="text-muted small">Complete your profile to start managing your invoices.</p>
                </div>

                <Formik
                    initialValues={{ name: "", businessType: "", countryOfOperation: "canada", location: "", phone: { countryCode: "+1", number: "" }, businessEmail: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {(formik) => (
                        <Form>
                            {/* Business Name */}
                            <BtForm.Group className="mb-3">
                                <BtForm.Label className="small fw-bold">Business Name</BtForm.Label>
                                <InputGroup className="bg-light rounded-3 border">
                                    <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted"><LuBriefcase size={18} /></InputGroup.Text>
                                    <BtForm.Control name="name" placeholder="Acme Corp" className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('name')} />
                                </InputGroup>
                                <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
                            </BtForm.Group>

                            {/* Business Type */}
                            <BtForm.Group className="mb-3">
                                <BtForm.Label className="small fw-bold">Business Type</BtForm.Label>
                                <BtForm.Select
                                    className="bg-light border shadow-none py-2 rounded-3"
                                    {...formik.getFieldProps('businessType')}
                                >
                                    <option value="">Select Type</option>
                                    <option value="retail">Retail</option>
                                    <option value="wholesale">Wholesale</option>
                                    <option value="services">Services</option>
                                    <option value="manufacturing">Manufacturing</option>
                                    <option value="ecommerce">E-Commerce</option>
                                    <option value="other">Other</option>
                                </BtForm.Select>
                                <ErrorMessage name="businessType" component="div" className="text-danger small mt-1" />
                            </BtForm.Group>

                            {/* Country of Operation */}
                            <BtForm.Group className="mb-3">
                                <BtForm.Label className="small fw-bold">Country of Operation</BtForm.Label>
                                <BtForm.Select
                                    className="bg-light border shadow-none py-2 rounded-3"
                                    {...formik.getFieldProps('countryOfOperation')}
                                >
                                    <option value="canada">Canada</option>
                                    <option value="nepal">Nepal</option>
                                </BtForm.Select>
                                <ErrorMessage name="countryOfOperation" component="div" className="text-danger small mt-1" />
                            </BtForm.Group>

                            {/* Public Email */}
                            <BtForm.Group className="mb-3">
                                <BtForm.Label className="small fw-bold">Business Email</BtForm.Label>
                                <InputGroup className="bg-light rounded-3 border">
                                    <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted"><LuMail size={18} /></InputGroup.Text>
                                    <BtForm.Control name="businessEmail" placeholder="info@business.com" className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('businessEmail')} />
                                </InputGroup>
                                <ErrorMessage name="businessEmail" component="div" className="text-danger small mt-1" />
                            </BtForm.Group>

                            {/* Phone */}
                            <PhoneInput
                                name="phone"
                                label="Phone"
                                value={formik.values.phone}
                                onChange={(val) => formik.setFieldValue('phone', val)}
                                error={formik.errors.phone?.number}
                                touched={formik.touched.phone?.number}
                                required
                            />

                            {/* Location */}
                            <BtForm.Group className="mb-4">
                                <BtForm.Label className="small fw-bold">Location Address</BtForm.Label>
                                <InputGroup className="bg-light rounded-3 border">
                                    <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted"><LuMapPin size={18} /></InputGroup.Text>
                                    <BtForm.Control name="location" placeholder="123 Street, City, Country" className="bg-transparent border-0 py-2 shadow-none" {...formik.getFieldProps('location')} />
                                </InputGroup>
                                <ErrorMessage name="location" component="div" className="text-danger small mt-1" />
                            </BtForm.Group>

                            <Button
                                variant="brand"
                                type="submit"
                                className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                                disabled={formik.isSubmitting || businessLoading}
                            >
                                {businessLoading ? "CREATING..." : "COMPLETE SETUP"}
                                <LuArrowRight size={18} />
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Card>
        </div>
    );
};

export default SetupBusiness;
